import { ReactNode, Ref, RefObject, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  components,
  DropdownIndicatorProps,
  InputProps,
  MenuListProps,
  MenuProps,
  MultiValueProps,
  OptionProps,
  PlaceholderProps,
  SingleValueProps,
} from "react-select";
import { List, RowComponentProps } from "react-window";
import cn from "classnames";
import { AppDOM } from "utils/app.utils";
import { OptionType, SelectProps } from "./CustomSelect.types";
import { CustomCheckbox } from "../CustomCheckbox";
import { AngleUp, Check } from "../Icons";
import s from "./CustomSelect.module.scss";

export const CustomDropdownIndicator = <T extends OptionType>({ className, ...props }: DropdownIndicatorProps<T>) => {
  return (
    <components.DropdownIndicator {...props}>
      <div className={cn(s.dropDownIndicator, className)}>
        <AngleUp />
      </div>
    </components.DropdownIndicator>
  );
};

export const CustomSingleValue = <T extends OptionType>({ className, ...props }: SingleValueProps<T>) => {
  return (
    <components.SingleValue {...props}>
      <div className={cn(s.singleValue, className)}>
        {(props.data.valueIcon ?? props.data.icon) && (
          <span className={s.valueIcon}>{props.data.valueIcon ?? props.data.icon}</span>
        )}
        {props.data.label && <span className={s.valueLabel}>{props.data.label}</span>}
      </div>
    </components.SingleValue>
  );
};

export const CustomMultiValue = <T extends OptionType>({ index, getValue, className }: MultiValueProps<T>) => {
  return !index && <div className={cn(s.multiValue, className)}>{getValue().length} items selected</div>;
};

type CustomOptionProps<T extends OptionType> = OptionProps<T> & {
  selectedIndicator: NonNullable<SelectProps<T>["selectedIndicator"]>;
};

export const CustomOption = <T extends OptionType>({ className, ...props }: CustomOptionProps<T>) => {
  const { isSelected, isFocused, selectedIndicator } = props;
  const selectedIndicatorMap: Record<CustomOptionProps<T>["selectedIndicator"], ReactNode> = {
    checkmark: isSelected && <Check />,
    checkbox: <CustomCheckbox checked={isSelected} />,
    none: null,
  };

  return (
    <components.Option {...props}>
      <div className={cn(s.option, { [s.isSelected]: isSelected }, { [s.isFocused]: isFocused }, className)}>
        <div className={s.textWrap}>
          {props.data.icon && <span className={s.valueIcon}>{props.data.icon}</span>}
          {props.data.label && <span className={s.valueLabel}>{props.data.label}</span>}
        </div>
        <div className={s.optionActions}>
          {selectedIndicatorMap[selectedIndicator]}
          {props.data.action && (
            <span className={s.action} onClick={(e) => e.stopPropagation()}>
              {props.data.action}
            </span>
          )}
        </div>
      </div>
    </components.Option>
  );
};

export const CustomPlaceholder = <T extends OptionType>({
  children,
  className,
  dataTestId,
  ...props
}: PlaceholderProps<T> & { dataTestId?: string }) => {
  const { selectProps } = props;

  if (selectProps.menuIsOpen) {
    return null;
  }

  return (
    <components.Placeholder {...props}>
      <div data-test-id={dataTestId} className={cn(s.placeholder, className)}>
        {children}
      </div>
    </components.Placeholder>
  );
};

export const CustomInput = <T extends OptionType>({ className, ...props }: InputProps<T>) => {
  const { isMulti, getValue } = props;

  return (
    <components.Input
      {...props}
      autoComplete="off"
      className={cn(s.input, { [s.isMulti]: isMulti }, { [s.isNonEmpty]: !!getValue().length }, className)}
    />
  );
};

const isReactNodeArray = (value: ReactNode): value is ReactNode[] => Array.isArray(value);

type MenuListRowData = { childrenArray: ReactNode[] };

const MenuListRow = ({ index, style, childrenArray }: RowComponentProps<MenuListRowData>) => (
  <div style={style}>{childrenArray[index]}</div>
);

export const CustomLazyLoadedMenuList = <T extends OptionType>({
  className,
  lazyLoadedMenuListRowHeight = 38, // 38px is the height of the option
  ...props
}: MenuListProps<T> & { lazyLoadedMenuListRowHeight?: number }) => {
  const { children, maxHeight } = props;
  const childrenArray = isReactNodeArray(children) ? children : [];

  return (
    <div className={cn(s.menuList, className)} style={{ height: maxHeight }}>
      <List
        rowCount={childrenArray.length}
        rowHeight={lazyLoadedMenuListRowHeight}
        rowProps={{ childrenArray }}
        rowComponent={MenuListRow}
      />
    </div>
  );
};

export const CustomMenuList = <T extends OptionType>({ className, ...props }: MenuListProps<T>) => {
  return <components.MenuList {...props} className={className} />;
};

type CustomMenuExtraProps = {
  portal?: boolean;
  controlRef: RefObject<HTMLElement | null>;
  menuRef: Ref<HTMLDivElement>;
  menuClassName?: string;
  menuPortalClassName?: string;
};

/** Same idea as ColorPicker: portal the dropdown and keep our CSS by wrapping with `.select`. */
export const CustomMenu = <T extends OptionType>({
  portal = true,
  controlRef,
  menuRef,
  menuClassName,
  menuPortalClassName,
  ...props
}: MenuProps<T> & CustomMenuExtraProps) => {
  const [anchor, setAnchor] = useState({ top: 0, left: 0, width: 0, height: 0 });

  useLayoutEffect(() => {
    if (!portal) return;

    const placeAnchor = () => {
      const control = controlRef.current?.querySelector<HTMLElement>(".react-select__control") ?? controlRef.current;
      const rect = control?.getBoundingClientRect();
      if (!rect) return;
      setAnchor({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
    };

    placeAnchor();
    window.addEventListener("resize", placeAnchor);
    window.addEventListener("scroll", placeAnchor, true);

    return () => {
      window.removeEventListener("resize", placeAnchor);
      window.removeEventListener("scroll", placeAnchor, true);
    };
  }, [portal, controlRef]);

  const menu = <components.Menu {...props} className={cn(props.className, menuClassName)} />;

  if (!portal) {
    return menu;
  }

  return createPortal(
    <div
      className={s.menuAnchor}
      style={{ top: anchor.top, left: anchor.left, width: anchor.width, height: anchor.height }}
    >
      <div
        className={cn(s.select, s.menuPortalDropdown, menuPortalClassName)}
        ref={menuRef as RefObject<HTMLDivElement>}
      >
        {menu}
      </div>
    </div>,
    AppDOM.appContainer ?? document.body
  );
};
